import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  HERE_API_KEY,
  IAddress,
  IAutocompleteResponse,
  IAutocompleteSuggestion,
  IGeocodingResponse,
  IHereMapAddress,
} from '@curbnturf/entities';
import { Observable, map, of } from 'rxjs';

/** Local port of @curbnturf/map's AddressHelper mapping logic — deliberately
 *  NOT imported from '@curbnturf/map' itself: that package only exposes a
 *  single barrel entrypoint (`libs/map/src/index.ts`), and importing anything
 *  from it pulls the whole library (map-modal, coordinates-map, etc.) into
 *  this app's TypeScript program, including long-unbuilt legacy code with
 *  pre-existing errors never surfaced because nothing in this app imported
 *  that library before. This mapping logic itself is pure and tiny, so a
 *  local copy is safer than the barrel import. */
function mapSuggestionToAddress(s: IAutocompleteSuggestion): IAddress {
  const a: IHereMapAddress | undefined = s.address;
  return {
    address: [a?.houseNumber, a?.street].filter(Boolean).join(' '),
    city: a?.city ?? '',
    state: a?.state ?? '',
    zipcode: a?.postalCode ?? '',
  };
}

function mapSuggestionToString(s: IAutocompleteSuggestion): string {
  const a = mapSuggestionToAddress(s);
  return [a.address, a.city, a.state, a.zipcode].filter(Boolean).join(' ');
}

/**
 * Plain-HttpClient HERE Maps client for the hosting wizard's address step.
 * Deliberately does NOT use @curbnturf/map's GeocodingService — that one routes
 * through CachedHttpClient, which pulls in NetworkFacade/AlertFacade (NgRx) plus
 * Ionic's AlertController/StatusFacade. Nothing else in this wizard uses NgRx;
 * same HERE endpoints, same API key, zero extra deps.
 */
@Injectable({ providedIn: 'root' })
export class ListingAddressGeocodingService {
  private http = inject(HttpClient);

  /** Suggestions for a partial address as the host types. Short-circuits below
   *  6 chars — matches the floor used by the reference HERE-typeahead flow. */
  autocomplete(partialAddress: string): Observable<IAutocompleteSuggestion[]> {
    if (partialAddress.trim().length < 6) return of([]);
    return this.http
      .get<IAutocompleteResponse>(
        `https://autocomplete.search.hereapi.com/v1/autocomplete?apiKey=${HERE_API_KEY}&in=countryCode:USA&q=${encodeURIComponent(
          partialAddress,
        )}`,
      )
      .pipe(map((r) => r?.items ?? []));
  }

  /** Forward-geocode a full address string to lat/lng. */
  geocode(fullAddress: string): Observable<{ lat: number; lng: number } | null> {
    return this.http
      .get<IGeocodingResponse>(
        `https://geocode.search.hereapi.com/v1/geocode?apiKey=${HERE_API_KEY}&q=${encodeURIComponent(fullAddress)}`,
      )
      .pipe(
        map((r) => {
          const pos = r?.items?.[0]?.position;
          return pos ? { lat: pos.lat, lng: pos.lng } : null;
        }),
      );
  }

  suggestionToAddress(s: IAutocompleteSuggestion): IAddress {
    return mapSuggestionToAddress(s);
  }

  suggestionToString(s: IAutocompleteSuggestion): string {
    return mapSuggestionToString(s);
  }
}
